// This global pack is loaded for every page in the app (public, operator, crew, etc) and every online app and native app webview.
// This pack is rendered inline with the page head and should be executed before any other packs.
// It's a good place to put critical constants, helpers and components that are used across the board. But be very selective as we want to keep the bundle size very small.

// - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// `rankersNamespace` function provides a consistent way to access the Rankers namespace on the global window object.
import {getRankersNamespace} from "../rankers/constants/rankers_namespace";
window.getRankersNamespace = getRankersNamespace; // Expose this function on the global window object.

// These functions allow for consistent access to the Rankers user agent header value on the global window object.
// The value is set on every page load that uses the `/app/views/layouts/shared/_head.html.erb` partial.
import {setRankersUserAgent, getRankersUserAgent} from "../rankers/constants/rankers_user_agent";
getRankersNamespace().setRankersUserAgent = setRankersUserAgent;
getRankersNamespace().getRankersUserAgent = getRankersUserAgent;

// These functions surface the server-resolved RequestContext entity to JS.
// The value is set on every page load that uses `_head.html.erb`. Used by the RUM
// beacon to label each beacon with the originating page's entity (the beacon POST
// to /rum/lcp can't be re-derived server-side without losing the page's namespace).
import {setRankersEntity, getRankersEntityTypeCode, getRankersEntityCode} from "../rankers/constants/rankers_entity";
getRankersNamespace().setRankersEntity = setRankersEntity;
getRankersNamespace().getRankersEntityTypeCode = getRankersEntityTypeCode;
getRankersNamespace().getRankersEntityCode = getRankersEntityCode;

// RequestContext - client-side mirror of Ruby's RequestContext for entity/platform detection.
import {requestContext} from "../rankers/lib/request_context";
getRankersNamespace().requestContext = requestContext;

// announceSessionChange() — the global self-login "announce" primitive
// (plans/2026-07-09--self-login-surface-session-announce.md). Deliberately dependency-light
// (imports NO session_state / emitter code) so it stays weightless on this universal bundle;
// it reads the shared detector off the namespace at call time. Fires on EVERY platform
// (plans/2026-07-14--session-announce-all-platforms.md — the native half is guarded inside the
// detector by the presence of a signalEmitter, not here). Full exposition in
// announce_session_change.js.
import { announceSessionChange } from "../rankers/lib/announce_session_change";
getRankersNamespace().announceSessionChange = announceSessionChange;

// The SessionStateDetector itself — the ONE copy of it in the whole app
// (plans/2026-07-14--session-detector-namespace-promotion.md). This is the only module in the
// codebase that imports the implementation. Consumers (nav, rankometer, member_offer_acceptor,
// account_panel — and, in future, any login-aware component on any page) import the
// `lib/session_state` SHIM, which reads these two off the namespace at call time. Before this,
// each of them inlined its own copy: esbuild is `splitting: false` / IIFE, so an imported module
// is duplicated into every bundle that imports it. This is heavier on the universal bundle than
// the announce stub above, and worth it: three copies could be live at once on one page.
import { observeSessionState, getSharedSessionStateDetector } from "../rankers/lib/session_state_detector";
getRankersNamespace().observeSessionState = observeSessionState;
getRankersNamespace().getSharedSessionStateDetector = getSharedSessionStateDetector;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Import Rankers helper functions
// Helpers are small utility functions.
// These functions must be imported and then exposed on the window object so the can continue to be accessed via the global scope.
// We should nest them inside the `window.RankersHelpers` object to avoid collisions.
window.RankersHelpers ??= {};

// The following helpers are used by scripts and components that are loaded after the global pack.

import DOMready from '../rankers/helpers/dom_ready';
window.RankersHelpers.DOMready = DOMready;

import DOMUpdateEvent from "../rankers/helpers/dom_update_event";
window.RankersHelpers.DOMUpdateEvent = DOMUpdateEvent;

import {injectLibrary, injectStylesheet} from "../rankers/helpers/injection";
window.RankersHelpers.injectLibrary = injectLibrary;
window.RankersHelpers.injectStylesheet = injectStylesheet;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// RUM (Real User Monitoring) — LCP attribution beacon.
// Posts a per-page-view LCP breakdown to /rum/lcp via navigator.sendBeacon.
// Self-skips on native-app webviews. See plans/2026-06-04--core-web-vitals-lcp-optimization.md.
import {initLcpBeacon} from "../rankers/lib/rum";
initLcpBeacon();
